import { ErrorCodes } from '../constants/error-codes'
import type { GmailConnectionState, McpIssue } from '../schemas/mcp'

/**
 * Cold-start-safe remediation links for the remote MCP assistant surface
 * (epic #524, story #525).
 *
 * Every `action_url` returned to an assistant must be cold-start-safe: it opens
 * in a fresh browser, triggers PureBox login when needed, then deep-links to the
 * intended action. It carries `?source=assistant&intent=...` and contains **no
 * tokens or secrets**. This builder is the single source of truth for that URL
 * shape so no caller can hand-roll an unsafe link.
 */

/**
 * Intents the assistant can deep-link a user into. The full set is defined here
 * (foundational, reused by later slices) even though story #525 only emits
 * `connect-gmail`, `reconnect-gmail`, and `signup`.
 */
export const REMEDIATION_INTENTS = [
  'signup',
  'link-assistant',
  'connect-gmail',
  'reconnect-gmail',
  'start-scan',
  'resume-scan',
  'retry-scan',
  'checkout',
  'portal',
] as const

export type RemediationIntent = (typeof REMEDIATION_INTENTS)[number]

/**
 * Web route each intent deep-links into. The web app owns resolving the intent
 * (and any Google OAuth flow); the assistant never performs the OAuth itself.
 */
const INTENT_PATHS: Record<RemediationIntent, string> = {
  signup: '/auth/sign-up',
  'link-assistant': '/account',
  'connect-gmail': '/account',
  'reconnect-gmail': '/account',
  'start-scan': '/dashboard',
  'resume-scan': '/dashboard',
  'retry-scan': '/dashboard',
  checkout: '/pricing',
  portal: '/account',
}

/** Reserved query keys this builder controls; callers cannot override them. */
const RESERVED_PARAMS = new Set(['source', 'intent'])

/** Matches anything that smells like a secret/token query key - rejected. */
const SECRET_PARAM_PATTERN = /token|secret|code|verifier|challenge|assertion|password|bearer|auth/i

export interface BuildRemediationUrlOptions {
  /**
   * Extra, non-secret query params (e.g. `{ prompt: 'consent' }` for a Google
   * re-grant). Reserved or secret-looking keys throw - links must stay clean.
   */
  params?: Record<string, string>
}

/**
 * Build a cold-start-safe remediation `action_url`.
 *
 * @param appBaseUrl Public web app origin, e.g. `https://app.purebox.io`.
 * @param intent     What the user should be deep-linked to do.
 * @param options    Optional extra non-secret query params.
 * @throws if `appBaseUrl` is not a valid http(s) URL, or a param key is
 *         reserved or secret-looking.
 */
export function buildRemediationUrl(
  appBaseUrl: string,
  intent: RemediationIntent,
  options: BuildRemediationUrlOptions = {},
): string {
  let base: URL
  try {
    base = new URL(appBaseUrl)
  } catch {
    throw new Error(`buildRemediationUrl: invalid appBaseUrl '${appBaseUrl}'`)
  }
  if (base.protocol !== 'http:' && base.protocol !== 'https:') {
    throw new Error(`buildRemediationUrl: appBaseUrl must be http(s), got '${base.protocol}'`)
  }

  const url = new URL(INTENT_PATHS[intent], base)
  url.search = ''
  url.searchParams.set('source', 'assistant')
  url.searchParams.set('intent', intent)

  for (const [key, rawValue] of Object.entries(options.params ?? {})) {
    if (RESERVED_PARAMS.has(key)) {
      throw new Error(`buildRemediationUrl: '${key}' is a reserved param`)
    }
    if (SECRET_PARAM_PATTERN.test(key)) {
      throw new Error(`buildRemediationUrl: param '${key}' looks token-adjacent and is not allowed`)
    }
    url.searchParams.set(key, rawValue)
  }

  return url.toString()
}

/**
 * Map a non-secret Gmail connection state to its Layer-2 `needs_user_action`
 * issue, with cold-start-safe remediation. Returns `null` for `connected`
 * (no action needed). This demonstrates the envelope end-to-end without ever
 * exposing a token.
 */
export function gmailNeedsActionIssue(
  state: GmailConnectionState,
  appBaseUrl: string,
): McpIssue | null {
  switch (state) {
    case 'connected':
      return null
    case 'none':
      return {
        code: ErrorCodes.GMAIL_TOKEN_MISSING.code,
        title: 'Connect Gmail to PureBox',
        detail: 'PureBox is not connected to your Gmail yet, so there is nothing to report.',
        remediation: 'Open PureBox and connect your Gmail account, then try again.',
        action_url: buildRemediationUrl(appBaseUrl, 'connect-gmail'),
        retryable: true,
      }
    case 'expired':
    case 'revoked':
      return {
        code: ErrorCodes.GMAIL_TOKEN_EXPIRED.code,
        title: 'Reconnect Gmail to PureBox',
        detail: 'PureBox lost access to your Gmail account. Reconnect to restore access.',
        remediation: 'Open PureBox and reconnect your Gmail account, then try again.',
        action_url: buildRemediationUrl(appBaseUrl, 'reconnect-gmail'),
        retryable: true,
      }
  }
}
